import React, { createContext, ReactNode, useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import {
  getStoredAccessToken,
  getStoredRefreshToken,
  removeStoredAccessToken,
  removeStoredRefreshToken,
  storeAccessToken,
  storeRefreshToken,
} from '../utils/authToken';

interface Auth {
  isLoggedIn: boolean;
  tempAccessToken?: string;
  ephemeralToken?: string;
  login: (data: LoginData) => void;
  loginCode: (data: LoginCodeData) => void;
  logout: () => void;
  refreshAccessToken: () => void;
}

interface LoginData {
  email: string;
  password: string;
}

interface LoginCodeData {
  ephemeralToken: string;
  code: string;
  refCode: string | null;
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
}

interface Login2FAResponse {
  ephemeralToken: string;
  method: string;
  otherMethods: string[];
}

interface TokenRefreshResponse {
  access: string;
  refresh: string;
}

const defaultAuth = {
  isLoggedIn: false,
  tempAccessToken: undefined,
  ephemeralToken: undefined,
  login: () => null,
  loginCode: () => null,
  logout: () => null,
  refreshAccessToken: () => null,
};

const authContext = createContext<Auth>(defaultAuth);

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export const useAuth = () => {
  return useContext(authContext);
};

function useProvideAuth() {
  const [isLoggedIn, setLoggedIn] = useState(Boolean(getStoredAccessToken()));
  // access token used before 2FA is activated
  const [tempAccessToken, setTempAccessToken] = useState<string | undefined>(undefined);
  const [ephemeralToken, setEphemeralToken] = useState<string | undefined>(undefined);

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const login = async (data: LoginData) => {
    const response = await axios
      .post('/api/auth/login/', data)
      .then((res) => res.data as LoginResponse | Login2FAResponse);
    if ('accessToken' in response) {
      const { accessToken: token } = response;
      setTempAccessToken(token);
    } else {
      const { ephemeralToken: token } = response;
      setEphemeralToken(token);
    }
    await queryClient.invalidateQueries(['fetchMe']);
  };

  const loginCode = async (data: LoginCodeData) => {
    const { accessToken, refreshToken } = await axios
      .post('/api/auth/login/code/', data)
      .then((res) => res.data as LoginResponse);
    storeAccessToken(accessToken);
    storeRefreshToken(refreshToken);
    setLoggedIn(true);
    await queryClient.invalidateQueries(['fetchMe']);
  };

  const logout = () => {
    axios
      .post('/api/auth/logout/', {}, { headers: { Authorization: `JWT ${getStoredAccessToken()}` } })
      .then()
      .finally(() => {
        removeStoredAccessToken();
        removeStoredRefreshToken();
        setLoggedIn(false);
        queryClient.clear();
        window.location.replace('/login');
        enqueueSnackbar('Logged out', { variant: 'success' });
      });
  };

  const refreshAccessToken = async () => {
    const refreshToken = getStoredRefreshToken();
    const { access, refresh } = await axios
      .post('/api/auth/token/refresh/', { refresh: refreshToken })
      .then((res) => res.data as TokenRefreshResponse);
    storeAccessToken(access);
    storeRefreshToken(refresh);
  };

  return {
    isLoggedIn,
    tempAccessToken,
    ephemeralToken,
    login,
    loginCode,
    logout,
    refreshAccessToken,
  };
}
